import classNames from 'classnames';
import fp from 'lodash/fp';
import React from 'react';
import { Breadcrumb, BreadcrumbItem, Col, Grid, Row } from 'react-bootstrap';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { branch, defaultProps, lifecycle, renderComponent, withProps, withStateHandlers } from 'recompose';
import { bindActionCreators, compose } from '../../../../../node_modules/redux';
import { contentSorting, routes, searchTileType } from '../../../../constants';
import { getProgressGoalIcon, isManager, mapUserCpdCategoryProgress } from '../../../../services';
import { cpdCategorysActions } from '../../../../state/modules/areas-of-advice';
import { publisherActions } from '../../../../state/modules/publisher';
import { searchTileActions } from '../../../../state/modules/search-tile';
import { currentUserCpdDaysRemaining, currentUserSelector, userActions } from '../../../../state/modules/user';
import { FormField, GoalContentFilterGroup, SearchToggler } from '../../../components';
import Loading from '../../loading';
import ProgressCard from '../partials/progress-card';
import ContentList, { searchTileFilter } from './partials/content-list';

const GoalHome = (props) => {
  const goal = fp.get('total')(props.goal)
  const shortFall = fp.get('shortFall')(props.goal)
  const validCpd = fp.get('completed')(props.goal)
  const progress = validCpd / goal
  const GoalIcon = getProgressGoalIcon(fp.get('goal.name')(props.goal))
  const goalProgress = {
    goal,
    shortFall,
    validCpd,
    progress,
    icon: GoalIcon ? <GoalIcon className='progress-goal-icon' /> : null
  }
  return (
    <div className='base-layout'>
      <section id='header--training' className='layout--header'>
        <Grid>
          <Row className='no-gutter'>
            <Col xs={12} md={7}>
              <Breadcrumb>
                <BreadcrumbItem active>
                  {fp.get('goal.name')(props.goal)}
                </BreadcrumbItem>
              </Breadcrumb>
              <h1 className='flexbox align-center'>
                {GoalIcon ? <GoalIcon className='icon-green right-gap' /> : null}{fp.get('goal.name')(props.goal)}
              </h1>
              <Row className='no-gutter'>
                <Col xs={12} md={6} className='no-padding top-gap flexbox nowrap'>
                  <span className='right-gap'>
                    <SearchToggler onChange={props.onSearchChange} />
                  </span>
                  <GoalContentFilterGroup {...{
                    onApply: props.updateFilters
                  }} />
                </Col>
                <Col xs={12} md={5} mdOffset={1} className='no-padding'>
                  <form className={classNames('searchToggle searchDesktop', { block: props.displaySearchBlock })}>
                    <FormField id='sort-by' label='Sort by' type='select-item' required>
                      <select
                        id='sort-by'
                        value={props.sort}
                        required
                        onChange={props.onSortByChange}>
                        {
                          fp.map((sortingKey) =>
                            <option key={sortingKey} value={sortingKey}>{fp.get(sortingKey, contentSorting).displayName}</option>
                          )(fp.keys(contentSorting))
                        }
                      </select>
                    </FormField>
                  </form>
                </Col>
              </Row>
            </Col>
            <ProgressCard {...{ ...props.overallCpdProgress, ...goalProgress, showExternalCpd: false }} />
          </Row>
        </Grid>
      </section>
      <ComposedContentSection {...props} />
    </div>
  );
}

const SearchResult = ({ filter }) => <ContentList filter={filter} />

const ComposedContentSection = compose(
  branch(
    ({ isFetchingCpdCategory, isFetchingUserCpdCategorys }) => isFetchingCpdCategory || isFetchingUserCpdCategorys,
    renderComponent(Loading)
  )
)(SearchResult);

const mapStateToProps = (state) => ({
  currentUser: currentUserSelector(state),
  cpdDaysRemaining: currentUserCpdDaysRemaining(state),
  cpdCategorys: state.cpdCategorys.cpdCategorys,
  isFetchingCpdCategorys: state.cpdCategorys.isFetching,
  publishers: state.publisher.publishers,
  isFetchingPublishers: state.publisher.isFetching,
  userCpdCategoryProgress: state.user.cpdCategoryProgress,
  userCpdCategories: state.user.cpdCategories,
  isFetchingUserCpdCategorys: state.user.currentUserCpdCategorysIsFetching,
  isFetchingContent: state.searchTile.isFetching,
  industryCpdCategorys: state.industry.cpdCategorys,
  userState: state.user,
  goals: fp.get('user.profile.stat.goals')(state)
});

const mapDispatchToProps = (dispatch) => ({
  getCpdCategorys: bindActionCreators(cpdCategorysActions.cpdCategorys.get, dispatch),
  getSearchTiles: bindActionCreators(searchTileActions.searchTile.get, dispatch),
  getUserCpdCategorys: bindActionCreators(userActions.user.getCpdCategorys, dispatch),
  getPublishers: bindActionCreators(publisherActions.publishers.get, dispatch),
  getUserBookmarks: bindActionCreators(userActions.user.getUserBookmarks, dispatch),
})

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withProps(
    ({ match, goals }) => {
      const goalId = parseInt(match.params.goalId, 10)
      const goal = fp.find(
        ({ goal }) => goal.id === goalId
      )(goals)
      return {
        goal
      }
    }
  ),
  branch(
    ({ currentUser }) => isManager(currentUser),
    renderComponent(() => (
      <Redirect to={routes.app.teams.root} />
    ))
  ),
  withStateHandlers(
    {
      initiatedContentLoading: false,
      displaySearchBlock: false,
      searchQuery: '',
      sort: fp.head(fp.keys(contentSorting)),
      searchDebounce: undefined,
      filters: {}
    },
    {
      updateFilters: () => (filters) => ({ filters }),
      updateSearchDebound: () => (searchDebounce) => ({ searchDebounce }),
      onSortByChange: () => (e) => ({
        sort: e.target.value
      }),
      onSearchChange: () => (query) => ({
        searchQuery: query,
      }),
      onSearchIconClick: ({ displaySearchBlock }) => () => ({
        displaySearchBlock: !displaySearchBlock
      }),
      contentLoadingInitiated: () => () => ({
        initiatedContentLoading: true
      })
    }
  ),
  withProps(
    ({
      searchQuery,
      sort,
      filters,
      goal
    }) => {
      const cpdCategories = fp.join(',')(filters.cpdCategoryIds);
      const associations = fp.join(',')(filters.associations);
      const locations = fp.join(',')(filters.locations);
      const emptyUserCpdCategorys = null;
      const emptyPublishers = null;
      const emptyBusinessId = null;
      const emptyContentType = null;
      const filter = searchTileFilter(
        searchTileType.Content,
        cpdCategories,
        sort,
        emptyUserCpdCategorys,
        searchQuery,
        emptyPublishers,
        emptyBusinessId,
        associations,
        locations,
        filters.min,
        filters.max,
        emptyContentType,
        filters.isBookmarkedOnly,
      );
      return {
        filter: {
          ...filter,
          goals: fp.get('goal.id')(goal)
        }
      };
    }
  ),
  defaultProps(
    {
      userCpdCategoryProgress: {}
    }
  ),
  withProps(
    ({ cpdCategorys, userCpdCategoryProgress, userState }) => ({
      mappedCpdCategorys: fp.filter(
        ({ hasChild }) => !hasChild
      )(mapUserCpdCategoryProgress(cpdCategorys)(userCpdCategoryProgress)),
      userCpdCategoryIds: userState.currentUserCpdCategorys,
      allCpdCategorys: fp.map((cpdCategory) => ({
        cpdCategory: cpdCategory.id,
        title: cpdCategory.name
      }))(cpdCategorys),
      overallCpdProgress: {
        totalCpdCategorys: fp.get('cpdCategorys.length')(userCpdCategoryProgress),
        ...fp.get('primaryStat')(userCpdCategoryProgress),
        ...fp.get('derivedStat')(userCpdCategoryProgress),
      }
    })
  ),
  lifecycle({
    componentDidMount() {
      const {
        cpdCategory,
        isFetchingCpdCategory,
        mappedCpdCategorys,
        publishers,
        isFetchingPublishers,
        getCpdCategorys,
        getPublishers,
        getUserCpdCategorys,
        getSearchTiles,
        filter,
        getUserBookmarks,
      } = this.props;

      getSearchTiles(0, filter);
      getUserBookmarks();

      if (fp.isEmpty(cpdCategory) && !isFetchingCpdCategory) {
        getCpdCategorys();
      }
      if (fp.isEmpty(publishers) && !isFetchingPublishers) {
        getPublishers();
      }
      if (fp.isEmpty(mappedCpdCategorys)) {
        getUserCpdCategorys();
      }
    },
    componentDidUpdate(prevProps) {
      const {
        getSearchTiles,
        filter,
        searchDebounce,
        updateSearchDebound
      } = this.props

      if (!fp.isEqual(filter)(prevProps.filter)) {
        const debounced = fp.debounce(500)(() => {
          getSearchTiles(0, filter);
        });
        searchDebounce && searchDebounce.cancel();
        updateSearchDebound(debounced)
        debounced();
      }
    }
  })
)(GoalHome)


// WEBPACK FOOTER //
// ./src/containers/pages/app/training/goal-home.js